import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { startBaseCheckout } from "../lib/premium";
import { Button } from "../components/ui/button";
import { CreditCard, Mail, LogOut, Landmark, Copy, Check, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import TransferDetails from "../components/TransferDetails";

const ADMIN_EMAIL = "soporteclinped@expedienteelectronicoclinped.com";

export default function LicenseExpired() {
  const { user, logout } = useAuth();
  const [billing, setBilling] = useState(null);
  const [copied, setCopied] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get("/billing-info").then((r) => setBilling(r.data || {})).catch(() => setBilling({}));
  }, []);

  const payWithCard = async () => {
    setPaying(true);
    await startBaseCheckout();
  };

  const hasBank = billing && (billing.clabe || billing.bank || billing.account_holder);
  const copyClabe = async () => {
    try {
      await navigator.clipboard.writeText(billing.clabe);
      setCopied(true);
      toast.success("CLABE copiada");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { toast.error("No se pudo copiar"); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-5" data-testid="license-expired-screen">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-stone-100 p-8 text-center"
      >
        <div className="flex justify-center mb-6"><Logo size={40} /></div>
        <div className="w-16 h-16 mx-auto rounded-2xl bg-brand-terracotta/10 flex items-center justify-center mb-5">
          <CreditCard className="text-brand-terracotta" size={30} />
        </div>
        <h1 className="font-outfit font-bold text-2xl text-brand-ink mb-2">Actualiza tu pago</h1>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          Tu periodo de acceso ha finalizado. Para continuar usando ClinPed, realiza el pago de tu
          suscripción mensual (<span className="font-semibold text-brand-ink">${billing?.amount || "150"} MXN/mes</span>)
          por transferencia y avisa al administrador para reactivar tu cuenta.
        </p>

        {user?.directory_search_count > 0 && (
          <div data-testid="directory-search-alert"
            className="text-left bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5 flex items-start gap-3">
            <Users size={20} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-outfit font-bold text-brand-ink text-sm">
                {user.directory_search_count} {user.directory_search_count === 1 ? "paciente te buscó" : "pacientes te buscaron"} en el directorio
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                No pudieron contactarte porque tu cuenta está inactiva. Reactiva tu plan para recibir sus solicitudes de cita.
              </p>
            </div>
          </div>
        )}
        {hasBank ? (
          <div data-testid="billing-bank-info" className="text-left bg-brand-sand/20 rounded-2xl p-4 mb-5 space-y-2">
            <div className="flex items-center gap-2 text-brand-ink font-semibold text-sm">
              <Landmark size={16} className="text-brand-sage" /> Datos para transferencia (SPEI)
            </div>
            <Row label="Titular" value="ClinPed" />
            {billing.bank && <Row label="Banco" value={billing.bank} />}
            {billing.clabe && (
              <div className="flex items-center justify-between gap-2">
                <Row label="CLABE" value={billing.clabe} />
                <button onClick={copyClabe} data-testid="copy-clabe-btn" className="shrink-0 text-brand-terracotta hover:opacity-70">
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
            )}
            {billing.concept && <Row label="Concepto" value={billing.concept} />}
            {billing.note && <p className="text-xs text-muted-foreground pt-1">{billing.note}</p>}
          </div>
        ) : (
          <div className="text-left mb-5"><TransferDetails plan="base" /></div>
        )}

        <button
          onClick={payWithCard}
          disabled={paying}
          data-testid="pay-with-card-btn"
          className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-brand-terracotta text-white font-nunito font-semibold text-sm shadow-lg shadow-brand-terracotta/20 hover:opacity-90 transition-all mb-3 disabled:opacity-60"
        >
          {paying ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
          {paying ? "Redirigiendo…" : `Pagar con tarjeta ($${billing?.amount || "150"} MXN)`}
        </button>

        <a
          href={`mailto:${ADMIN_EMAIL}?subject=Reactivar%20suscripci%C3%B3n%20ClinPed&body=Hola,%20deseo%20reactivar%20mi%20cuenta%20(${encodeURIComponent(user?.email || "")}).`}
          data-testid="license-contact-admin-btn"
          className="w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-2xl border border-stone-200 text-brand-ink font-nunito font-semibold text-sm hover:bg-stone-50 transition-all mb-3"
        >
          <Mail size={16} /> Pagué por transferencia, avisar al admin
        </a>
        <p className="text-xs text-muted-foreground mb-6">{ADMIN_EMAIL}</p>
        <Button variant="ghost" onClick={logout} data-testid="license-logout-btn" className="text-muted-foreground gap-2">
          <LogOut size={16} /> Cerrar sesión
        </Button>
      </motion.div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="text-muted-foreground w-16 shrink-0">{label}:</span>
      <span className="font-semibold text-brand-ink break-all">{value}</span>
    </div>
  );
}
